import React, { memo } from 'react';
import { useParams } from 'react-router-dom';
import { Loader } from 'semantic-ui-react';
import useAVReviewRole from '../av-review-dashboard/hooks/use-av-review-role';
import useReviewSession from './hooks/use-review-session';
import HeaderBar from './components/header-bar';
import DetectionList from './components/detection-list';
import EventPillsBar from './components/event-pills-bar';
import ChunkNav from './components/chunk-nav';
import EvidenceViewer from './components/evidence-viewer';
import VerdictButtons from './components/verdict-buttons';
import DetectionSummary from './components/detection-summary';
import { Root, ContentWrapper, SidebarWrapper, MainWrapper, SubmitBar, SubmitBarButton, LoaderWrapper, NoAccessWrapper } from './av-review-detail-styled';
import { Separator, Text } from '@cuemath/web-comps';
import COLORS from 'constants/colors';

const AVReviewDetail = () => {
  const { reviewId } = useParams();
  const { role, isLoading: isRoleLoading } = useAVReviewRole();

  const {
    data,
    isLoading,
    isVerdictBusy,
    detections,
    reviewProgress,
    activeDetectionType,
    activeDetection,
    activeEventIndex,
    activeEvent,
    activeChunkIndex,
    activeChunk,
    nextVideoUrl,
    overlayText,
    viewingThresholdMet,
    isShowingSummary,
    isReReview,
    nextUnreviewedType,
    selectDetection,
    selectEvent,
    goToNextChunk,
    goToPrevChunk,
    selectChunk,
    markChunkViewed,
    isReviewSubmitted,
    submitVerdict,
    reReviewEvent,
    cancelReReview,
    goToNextDetection,
    allDetectionsVerdicted,
    submitBarSummary,
    submitReview,
    isSubmitProcessing,
  } = useReviewSession(reviewId);

  const canSubmitReview = allDetectionsVerdicted && !isReviewSubmitted;

  if (isRoleLoading || isLoading) {
    return (
      <LoaderWrapper justifyContent="center" alignItems="center">
        <Loader active inline="centered" />
      </LoaderWrapper>
    );
  }

  if (!role) {
    return <NoAccessWrapper>You do not have access to this page.</NoAccessWrapper>;
  }

  return (
    <Root>
      <HeaderBar review={data} reviewProgress={reviewProgress} />
      <ContentWrapper flexDirection="row">
        <SidebarWrapper>
          <DetectionList
            detections={detections}
            activeDetectionType={activeDetectionType}
            onSelectDetection={selectDetection}
          />
        </SidebarWrapper>
        <MainWrapper>
          {activeDetection && (
            <>
              <EventPillsBar
                events={activeDetection.events}
                activeEventIndex={isShowingSummary ? null : activeEventIndex}
                onSelectEvent={isShowingSummary ? reReviewEvent : selectEvent}
                isReviewSubmitted={isReviewSubmitted}
              />
              {isShowingSummary ? (
                <DetectionSummary
                  detection={activeDetection}
                  nextDetectionType={nextUnreviewedType}
                  onGoToNext={goToNextDetection}
                  onReReviewEvent={reReviewEvent}
                  isReviewSubmitted={isReviewSubmitted}
                />
              ) : (
                activeEvent && (
                  <>
                    <ChunkNav
                      eventIndex={activeEventIndex}
                      chunks={activeEvent.event_data}
                      activeChunkIndex={activeChunkIndex}
                      onSelectChunk={selectChunk}
                      onPrev={goToPrevChunk}
                      onNext={goToNextChunk}
                    />
                    {isReviewSubmitted ? <Separator height={8} /> : (
                      <VerdictButtons
                        event={activeEvent}
                        isThresholdMet={viewingThresholdMet}
                        isVerdictBusy={isVerdictBusy}
                        onSubmitVerdict={submitVerdict}
                        isReReview={isReReview}
                        onBackToSummary={cancelReReview}
                        detectionType={activeDetectionType}
                      />
                    )}
                    <EvidenceViewer
                      chunk={activeChunk}
                      overlayText={overlayText}
                      onPlay={markChunkViewed}
                      nextVideoUrl={nextVideoUrl}
                    />
                  </>
                )
              )}
            </>
          )}
          {canSubmitReview && (
            <SubmitBar flexDirection="row" alignItems="center" justifyContent="space-between">
              <Text variant="body2" color={COLORS.BLACK_T_60}>{submitBarSummary}</Text>
              <SubmitBarButton onClick={submitReview} disabled={isSubmitProcessing}>
                <Text variant="cta2">{isSubmitProcessing ? 'Submitting...' : 'Submit Review'}</Text>
              </SubmitBarButton>
            </SubmitBar>
          )}
        </MainWrapper>
      </ContentWrapper>
    </Root>
  );
};

export default memo(AVReviewDetail);
